import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { ArrowLeft, Pencil } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { cn } from '../lib/utils';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const profileSchema = z.object({
  name: z.string().min(1, 'El nombre es obligatorio').max(80, 'Máximo 80 caracteres'),
});

const PLAN_CREDIT_MAX = { free: 1000, pro: 5000, enterprise: 20000 };

function getInitials(name, email) {
  if (name) {
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email?.[0]?.toUpperCase() ?? '?';
}

function planVariant(planType) {
  if (planType === 'pro') return 'default';
  if (planType === 'enterprise') return 'secondary';
  return 'outline';
}

const Profile = () => {
  const { user, profile, refreshProfile } = useAuth();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const fileInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: { name: profile?.name ?? '' },
  });

  const openDialog = () => {
    reset({ name: profile?.name ?? '' });
    setAvatarFile(null);
    setAvatarPreview(null);
    setDialogOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const onSubmit = async ({ name }) => {
    setSaving(true);
    try {
      let avatar_url = profile?.avatar_url ?? null;

      if (avatarFile) {
        const path = `avatars/${user.id}/avatar`;
        const { error: uploadError } = await supabase.storage
          .from('prompt-images')
          .upload(path, avatarFile, { upsert: true });
        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('prompt-images')
          .getPublicUrl(path);
        avatar_url = urlData.publicUrl;
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ name, avatar_url, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (updateError) throw updateError;

      await refreshProfile();
      setDialogOpen(false);
      toast({ title: 'Perfil actualizado', description: 'Los cambios se guardaron correctamente.' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const planType = profile?.plan_type ?? 'free';
  const aiCredits = profile?.ai_credits ?? 0;
  const creditMax = PLAN_CREDIT_MAX[planType] ?? 1000;
  const creditPct = Math.min((aiCredits / creditMax) * 100, 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fafafa] via-[#f0f4ff] to-[#fdf4ff] dark:bg-none dark:bg-[#0d0d0f] transition-colors duration-300">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl dark:bg-[#0d0d0f] border-b border-indigo-50 dark:border-[#1a1a1f] sticky top-0 z-50 transition-colors duration-300">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/dashboard">
            <Button
              variant="ghost"
              size="icon"
              className="text-[#6b7280] hover:text-[#1a1625] hover:bg-indigo-50/50 dark:text-[#888] dark:hover:text-[#e5e5e5] dark:hover:bg-[#1a1a20]"
            >
              <ArrowLeft size={20} />
            </Button>
          </Link>
          <h1 className="text-lg font-semibold text-[#1a1625] dark:text-[#e5e5e5]">Mi perfil</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Hero card */}
        <div className="bg-white/75 backdrop-blur-md border border-white/90 shadow-[0_4px_20px_rgba(99,102,241,0.06)] dark:bg-[#111114] dark:border-[#1e1e24] dark:shadow-none rounded-2xl p-8 transition-colors duration-300">
          <div className="flex items-center gap-6">
            <div className="p-1 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 shadow-lg shadow-indigo-200 dark:shadow-none">
              <Avatar className="h-20 w-20 text-2xl ring-2 ring-white dark:ring-[#111114]">
                <AvatarImage src={profile?.avatar_url} alt={profile?.name} />
                <AvatarFallback className="text-xl bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-[#1a1a20] dark:to-[#111114] text-indigo-600 dark:text-violet-400">
                  {getInitials(profile?.name, user?.email)}
                </AvatarFallback>
              </Avatar>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-[#1a1625] dark:text-[#e5e5e5] truncate">
                {profile?.name || 'Sin nombre'}
              </h2>
              <p className="text-sm text-[#6b7280] dark:text-[#888] truncate">{user?.email}</p>
            </div>
            <Button
              onClick={openDialog}
              className="shrink-0 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white border-0 shadow-md"
            >
              <Pencil size={16} className="mr-2" />
              Editar perfil
            </Button>
          </div>
        </div>

        {/* Plan activo */}
        <div className="bg-white/75 backdrop-blur-md border border-white/90 shadow-[0_4px_20px_rgba(99,102,241,0.06)] dark:bg-[#111114] dark:border-[#1e1e24] dark:shadow-none rounded-2xl p-6 transition-colors duration-300">
          <p className="text-xs font-medium text-[#6b7280] dark:text-[#888] uppercase tracking-wider mb-2">
            Plan activo
          </p>
          <Badge variant={planVariant(planType)} className="text-sm px-3 py-1 capitalize">
            {planType}
          </Badge>
        </div>

        {/* Créditos de IA */}
        <div className="bg-white/75 backdrop-blur-md border border-white/90 shadow-[0_4px_20px_rgba(99,102,241,0.06)] dark:bg-[#111114] dark:border-[#1e1e24] dark:shadow-none rounded-2xl p-6 space-y-3 transition-colors duration-300">
          <p className="text-xs font-medium text-[#6b7280] dark:text-[#888] uppercase tracking-wider">
            Créditos de IA disponibles
          </p>
          <Progress value={creditPct} className="h-3" />
          <p className="text-sm text-[#1a1625] dark:text-[#e5e5e5]">
            <span className="font-semibold">{aiCredits.toLocaleString()}</span>
            {' '}/ {creditMax.toLocaleString()} créditos
          </p>
        </div>
      </main>

      {/* Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-[#111114] border-white/90 dark:border-[#1e1e24]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1625] dark:text-[#e5e5e5]">Editar perfil</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 py-2">
            {/* Avatar preview + file input */}
            <div className="flex flex-col items-center gap-3">
              <div className="p-1 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500">
                <Avatar className="h-20 w-20 ring-2 ring-white dark:ring-[#111114]">
                  <AvatarImage
                    src={avatarPreview ?? profile?.avatar_url}
                    alt="Preview"
                  />
                  <AvatarFallback className="text-xl bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-[#1a1a20] dark:to-[#111114] text-indigo-600 dark:text-violet-400">
                    {getInitials(profile?.name, user?.email)}
                  </AvatarFallback>
                </Avatar>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-indigo-100 dark:border-[#1e1e24] text-indigo-600 dark:text-violet-400 hover:bg-indigo-50 dark:hover:bg-[#1a1a20]"
                onClick={() => fileInputRef.current?.click()}
              >
                Cambiar foto
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            {/* Name field */}
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-[#1a1625] dark:text-[#e5e5e5]">Nombre</Label>
              <Input
                id="name"
                {...register('name')}
                className={cn(
                  'border-indigo-100 dark:border-[#1e1e24] bg-white dark:bg-[#1a1a20] text-[#1a1625] dark:text-[#e5e5e5] focus:border-indigo-400 dark:focus:border-violet-500',
                  errors.name && 'border-destructive'
                )}
              />
              {errors.name && (
                <p className="text-xs text-destructive">{errors.name.message}</p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="border-indigo-100 dark:border-[#1e1e24]"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white border-0"
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
